import type { CSSProperties, ReactNode } from 'react';
import { AlertIcon, CheckIcon, ClockIcon, XIcon } from './icons.js';

/**
 * Shared primitives for the three frontends.
 *
 * Status is always conveyed by an icon plus a word plus a colour, never by
 * colour alone - the same rule applies whether the reader is colour blind or
 * looking at a printed statement.
 */

const TONES = {
  ok: { fg: 'var(--wc-green)', bg: 'var(--wc-green-tint)', bd: 'var(--wc-green-line)' },
  pending: { fg: 'var(--wc-amber)', bg: 'var(--wc-amber-tint)', bd: 'var(--wc-amber-line)' },
  fail: { fg: 'var(--wc-red)', bg: 'var(--wc-red-tint)', bd: 'var(--wc-red-line)' },
  warn: { fg: 'var(--wc-orange)', bg: 'var(--wc-orange-tint)', bd: 'var(--wc-orange-line)' },
  info: { fg: 'var(--wc-blue)', bg: 'var(--wc-blue-tint-2)', bd: 'var(--wc-blue-line)' },
  neutral: { fg: 'var(--wc-grey)', bg: 'var(--wc-grey-tint)', bd: 'var(--wc-grey-line)' },
} as const;

export type Tone = keyof typeof TONES;

/** Which tone a payment state carries, in one place so it cannot drift. */
export function toneForState(state: string): Tone {
  switch (state) {
    case 'SUCCEEDED':
      return 'ok';
    case 'PROCESSING':
    case 'REQUIRES_ACTION':
    case 'REQUIRES_PAYMENT_METHOD':
    case 'CREATED':
      return 'pending';
    case 'FAILED':
      return 'fail';
    case 'CANCELLED':
      return 'neutral';
    case 'REFUNDED':
    case 'PARTIALLY_REFUNDED':
      return 'info';
    case 'DISPUTED':
      return 'warn';
    default:
      return 'neutral';
  }
}

export function Badge({
  tone = 'neutral',
  children,
  icon = true,
}: {
  tone?: Tone;
  children: ReactNode;
  icon?: boolean;
}) {
  const { fg, bg, bd } = TONES[tone];
  const Icon =
    tone === 'ok' ? CheckIcon
    : tone === 'fail' ? XIcon
    : tone === 'pending' ? ClockIcon
    : tone === 'warn' ? AlertIcon
    : null;

  return (
    <span className="wc-badge" style={{ color: fg, background: bg, borderColor: bd }}>
      {icon && Icon ? <Icon size={12} /> : null}
      {children}
    </span>
  );
}

/**
 * `label` lets a panel show the state in its own language while the tone still
 * comes from the raw value. The raw value is kept as the `title`, because that
 * is the string you quote in a support ticket or search for in the audit log.
 */
export function StateBadge({ state, label }: { state: string; label?: string }) {
  return (
    <span title={state}>
      <Badge tone={toneForState(state)}>{label ?? state.replace(/_/g, ' ')}</Badge>
    </span>
  );
}

export function Panel({
  title,
  actions,
  children,
  padded = true,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  padded?: boolean;
}) {
  return (
    <section className="wc-panel">
      {title ? (
        <header className="wc-panel-head">
          <h2>{title}</h2>
          {actions}
        </header>
      ) : null}
      {padded ? <div className="wc-panel-body">{children}</div> : children}
    </section>
  );
}

export function Stat({
  label,
  value,
  hint,
  accent = 'var(--wc-blue)',
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  accent?: string;
}) {
  return (
    <div className="wc-stat" style={{ borderLeftColor: accent }}>
      <div className="wc-stat-label">{label}</div>
      <div className="wc-stat-value">{value}</div>
      {hint ? <div className="wc-stat-hint">{hint}</div> : null}
    </div>
  );
}

export function Note({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'warn' | 'danger' | 'ok';
  title?: ReactNode;
  children: ReactNode;
}) {
  const cls =
    tone === 'warn' ? 'wc-note wc-note-warn'
    : tone === 'danger' ? 'wc-note wc-note-danger'
    : tone === 'ok' ? 'wc-note wc-note-ok'
    : 'wc-note';
  return (
    <div className={cls} role={tone === 'danger' ? 'alert' : undefined}>
      {title ? <strong style={{ display: 'block', marginBottom: 3 }}>{title}</strong> : null}
      {children}
    </div>
  );
}

export function Field({
  label,
  hint,
  action,
  children,
}: {
  label: string;
  hint?: ReactNode;
  /**
   * A control that belongs beside the input rather than under it.
   *
   * The alternative - putting the button next to the whole Field and nudging it
   * with a margin - lines it up against the bottom of the *hint*, so the same
   * markup sits correctly on a field without a hint and a few pixels low on one
   * with. Rendering it inside the field row makes the alignment structural.
   */
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <label className="wc-field">
      <span className="wc-label">{label}</span>
      {action ? (
        <span style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
          <span style={{ flex: 1, minWidth: 0, display: 'block' }}>{children}</span>
          <span style={{ flex: 'none', display: 'flex' }}>{action}</span>
        </span>
      ) : (
        children
      )}
      {hint ? (
        <span style={{ display: 'block', marginTop: 4, fontSize: 12.5, color: 'var(--wc-faint)' }}>
          {hint}
        </span>
      ) : null}
    </label>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return (
    <div style={{ padding: '40px 16px', textAlign: 'center', color: 'var(--wc-faint)', fontSize: 14 }}>
      {children}
    </div>
  );
}

export function Spinner({ label }: { label: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--wc-muted)', fontSize: 14 }}
    >
      <span
        aria-hidden
        style={{
          width: 14,
          height: 14,
          border: '2px solid var(--wc-border-strong)',
          borderTopColor: 'var(--wc-blue)',
          borderRadius: '50%',
          animation: 'wc-spin 0.7s linear infinite',
          display: 'inline-block',
        }}
      />
      {label}
    </div>
  );
}

export function Mono({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <span className="wc-num" style={style}>
      {children}
    </span>
  );
}

export function Grid({ cols, children }: { cols: number; children: ReactNode }) {
  return (
    <div className="wc-grid" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
      {children}
    </div>
  );
}

/** Tables scroll inside their own box so the page body never scrolls sideways. */
export function TableWrap({ children }: { children: ReactNode }) {
  return <div className="wc-scroll-x">{children}</div>;
}
